import * as stylex from '@stylexjs/stylex';
import { useEffect } from 'react';
import { measureComponentLoad } from './utils/WebVitals';
import { useGPUAcceleratedProps } from './utils/AnimationOptimizer';
import {
  Flex,
  Box
} from '@radix-ui/themes';
import Background from './Background';
import Analytics from './components/common/Analytics';
import LanguageSwitcher from './components/common/LanguageSwitcher';
import ErrorBoundary from './components/common/ErrorBoundary';
import SeoManager from './components/common/SeoManager';
import { initSmoothScroll } from './utils/smoothScroll';

// Import sections directly instead of lazy loading
import AlbumSection from './components/sections/AlbumSection';
import ArtistSection from './components/sections/ArtistSection';
import AboutAuthorSection from './components/sections/AboutAuthorSection';

// Maintaining some stylex for custom styling needs
const styles = stylex.create({
  mainWrapper: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'flex-start',
    position: 'relative',
    zIndex: 1,
  },
  mainContent: {
    width: '100%',
  },
});

function App() {
  // Measure component load time
  const endMeasure = measureComponentLoad('App');

  // Get GPU-accelerated properties for better animation performance
  const gpuProps = useGPUAcceleratedProps();

  // Initialize smooth scroll when component mounts
  useEffect(() => {
    // Init smooth scroll
    initSmoothScroll();

    // Report app component mounted
    endMeasure();
  }, [endMeasure]);

  return (
    <ErrorBoundary>
      {/* SEO Metadata Management */}
      <SeoManager page="home" />

      <Analytics />
      <LanguageSwitcher />
      <Background />
      <div
        {...stylex.props(styles.mainWrapper)}
        style={{
          ...gpuProps,
        }}
      >
        <Box asChild width="100%">
          <main id="main-content" {...stylex.props(styles.mainContent)}>
            <Box
              width="100%"
              maxWidth="none"
              // px={{ initial: '4', sm: '6', md: '8' }}
              mt="4"
            >
              <Flex
                id="album-section"
                tabIndex={-1}
                direction={{ initial: 'column-reverse', md: 'row' }}
                gap={{ initial: '6', md: '8' }}
                justify="center"
                align="center"
                mb="8"
              >
                <Box
                  style={{ flex: '1' }}
                  width={{ initial: '100%', md: 'auto' }}
                  maxWidth="600px"
                  mt="6"
                >
                  <Flex
                    align="center"
                    justify="center"
                    direction="column"
                  >
                    <ErrorBoundary>
                      <AlbumSection />
                    </ErrorBoundary>
                  </Flex>
                </Box>
                <Box
                  style={{ flex: '1' }}
                  width={{ initial: '100%', md: 'auto' }}
                  maxWidth="600px"
                >
                  <ErrorBoundary>
                    <ArtistSection />
                  </ErrorBoundary>
                </Box>
              </Flex>

              <Box
                id="artist-section"
                tabIndex={-1}
                mb="8"
              >
                <ErrorBoundary>
                  <AboutAuthorSection />
                </ErrorBoundary>
              </Box>
            </Box>
          </main>
        </Box>
      </div>
    </ErrorBoundary>
  );
}

export default App;
